import { type QueryObserverResult, useQuery } from '@tanstack/react-query'
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useEmbeddedOnboarding } from 'components/hooks/employer_onboarding/useEmbeddedOnboarding'
import { getEmployersEmployersId, getGetEmployersEmployersIdQueryKey, useGetPartnersEmbeddedOnboardingSessionStatus } from 'utils/web_api'
import { type EmployersEmployer as Employer } from 'utils/web_api.schemas'

interface EmployerContextType {
  employer: Employer | null;
  error: Error | null;
  loading: boolean;
  refetch: ReturnType<typeof useQuery>['refetch'];
  setEmployerId: (id: string | null) => void;
}

export const EmployerContext = createContext<EmployerContextType>({
  employer: null,
  error: null,
  loading: false,
  refetch: () => Promise.resolve({ data: null, error: null, isLoading: false } as QueryObserverResult<unknown, unknown>),
  setEmployerId: () => {},
})

export function useEmployer () {
  return useContext(EmployerContext)
}

interface EmployerProviderProps {
  children: React.ReactNode | ((props: EmployerContextType) => React.ReactNode);
  employerId: string | null;
}

export function EmployerProvider ({
  children,
  employerId,
}: EmployerProviderProps) {
  const [id, setId] = useState<string | null>(employerId)
  const { isEmbedded } = useEmbeddedOnboarding()

  // For embedded onboarding, poll session status until finalized
  // This allows us to refetch employer data once the async job completes
  const { data: sessionStatus } = useGetPartnersEmbeddedOnboardingSessionStatus({
    query: {
      enabled: isEmbedded,
      refetchInterval: (query) => {
        const finalized = query.state.data?.finalized_at
        return finalized ? false : 1000
      },
    },
  })

  const isSessionFinalized = !isEmbedded || !!sessionStatus?.finalized_at

  const { data: employer, isLoading, error, refetch } = useQuery({
    queryKey: id ? getGetEmployersEmployersIdQueryKey(id) : [],
    queryFn: () => (id ? getEmployersEmployersId(id) : null),
    enabled: !!id,
  })

  // Track if we've already refetched employer data after finalization
  const hasRefetchedAfterFinalizeRef = useRef(false)

  // Refetch employer data when session becomes finalized to get updated name/industry from async job
  useEffect(() => {
    if (isSessionFinalized && isEmbedded && !hasRefetchedAfterFinalizeRef.current) {
      hasRefetchedAfterFinalizeRef.current = true
      refetch()
    }
  }, [isSessionFinalized, isEmbedded, refetch])

  const value = useMemo(
    () => ({
      employer: employer || null,
      error,
      loading: isLoading,
      refetch,
      setEmployerId: setId,
    }),
    [employer, isLoading, error, refetch]
  )

  return (
    <EmployerContext.Provider value={value}>
      {typeof children === 'function' ? children(value) : children}
    </EmployerContext.Provider>
  )
}
